import { DashboardState, IDevice } from "@kuika/kuika-cl-model";
import { IScreenCanvasSize } from "@kuika/kuika-cl-model/src/models/screen-canvas-sizes";
import _ from "lodash";
import React from "react";
import { IScreenDesignContextState } from "../../../contexts/screen-design-context/screen-design-context";
import deviceListJson from "../../../shared/data/device-list.json";
import { KMainFunctions } from "../../../shared/utility/main-functions";

export interface IDesignResizeButonState {
  isMouseOver: boolean;
  isMouseDown: boolean;
  rulerWidth?: number;
  rulerHeight?: number;
  isMouseOverToHeightButton: boolean;
  isMouseDownToHeightButton: boolean;
}

export interface IDesignResizeButtonProps {
  zoomScale: number;
  minWidth: number;
  minHeight: number;
  onChangeResize(): void;
  sd: IScreenDesignContextState;
}

export class DesignResize extends React.PureComponent<IDesignResizeButtonProps, IDesignResizeButonState> {
  private readonly deviceList: IDevice[] = deviceListJson as unknown as IDevice[];

  private mouseMoveHandler?: (event: MouseEvent) => void;

  private mouseUpHandler?: (event: MouseEvent) => void;

  private mouseMoveHeightHandler?: (event: MouseEvent) => void;

  private mouseUpHeightHandler?: (event: MouseEvent) => void;

  private debounceResize: _.DebouncedFunc<() => void> | null = null;

  // WeakRefs for DOM elements
  private layoutFrameRef: WeakRef<HTMLElement> | null = null;

  private designAreaRef: WeakRef<HTMLElement> | null = null;

  private resizeSectionDivRef: WeakRef<HTMLElement> | null = null;

  private designRulerRef: WeakRef<HTMLElement> | null = null;

  private resizeHeightSectionDivRef: WeakRef<HTMLElement> | null = null;

  constructor(props: IDesignResizeButtonProps) {
    super(props);
    this.state = {
      isMouseOver: false,
      isMouseDown: false,
      isMouseOverToHeightButton: false,
      isMouseDownToHeightButton: false
    };

    this.debounceResize = _.debounce(() => this.props.onChangeResize(), 1000 / 60);
  }

  componentDidMount() {
    this.mouseMoveHandler = this.handleMouseMove.bind(this);
    this.mouseUpHandler = this.handleMouseUp.bind(this);
    this.mouseMoveHeightHandler = this.handleMouseMoveForHeight.bind(this);
    this.mouseUpHeightHandler = this.handleMouseUpForHeight.bind(this);

    // Initialize WeakRefs
    this.initDomRefs();
  }

  componentWillUnmount() {
    this.removeEventListeners();

    if (this.debounceResize) {
      this.debounceResize.cancel();
    }

    // Clear references
    this.layoutFrameRef = null;
    this.designAreaRef = null;
    this.resizeSectionDivRef = null;
    this.designRulerRef = null;
    this.resizeHeightSectionDivRef = null;
  }

  private initDomRefs = () => {
    const layoutFrame = document.getElementById("kuika_iframeAgent_canvas");
    const designArea = document.getElementById("kuika_designArea");
    const resizeSectionDiv = document.getElementById("kuika_resizeSectionDiv");
    const designRuler = document.getElementById("kuika_design-ruler");
    const resizeHeightSectionDiv = document.getElementById("kuika_resizeHeightSectionDiv");

    if (layoutFrame) this.layoutFrameRef = new WeakRef(layoutFrame);
    if (designArea) this.designAreaRef = new WeakRef(designArea);
    if (resizeSectionDiv) this.resizeSectionDivRef = new WeakRef(resizeSectionDiv);
    if (designRuler) this.designRulerRef = new WeakRef(designRuler);
    if (resizeHeightSectionDiv) this.resizeHeightSectionDivRef = new WeakRef(resizeHeightSectionDiv);
  };

  private getLayoutFrame = (): HTMLElement | null => {
    let element = this.layoutFrameRef?.deref() || null;
    if (!element) {
      element = document.getElementById("kuika_iframeAgent_canvas");
      if (element) this.layoutFrameRef = new WeakRef(element);
    }
    return element;
  };

  private getDesignArea = (): HTMLElement | null => {
    let element = this.designAreaRef?.deref() || null;
    if (!element) {
      element = document.getElementById("kuika_designArea");
      if (element) this.designAreaRef = new WeakRef(element);
    }
    return element;
  };

  private getResizeSectionDiv = (): HTMLElement | null => {
    let element = this.resizeSectionDivRef?.deref() || null;
    if (!element) {
      element = document.getElementById("kuika_resizeSectionDiv");
      if (element) this.resizeSectionDivRef = new WeakRef(element);
    }
    return element;
  };

  private getDesignRuler = (): HTMLElement | null => {
    let element = this.designRulerRef?.deref() || null;
    if (!element) {
      element = document.getElementById("kuika_design-ruler");
      if (element) this.designRulerRef = new WeakRef(element);
    }
    return element;
  };

  private getResizeHeightSectionDiv = (): HTMLElement | null => {
    let element = this.resizeHeightSectionDivRef?.deref() || null;
    if (!element) {
      element = document.getElementById("kuika_resizeHeightSectionDiv");
      if (element) this.resizeHeightSectionDivRef = new WeakRef(element);
    }
    return element;
  };

  private removeEventListeners = () => {
    if (this.mouseMoveHandler) {
      window.removeEventListener("mousemove", this.mouseMoveHandler);
    }
    if (this.mouseUpHandler) {
      window.removeEventListener("mouseup", this.mouseUpHandler);
    }
    if (this.mouseMoveHeightHandler) {
      window.removeEventListener("mousemove", this.mouseMoveHeightHandler);
    }
    if (this.mouseUpHeightHandler) {
      window.removeEventListener("mouseup", this.mouseUpHeightHandler);
    }
  };

  addEventListenerMoveAndUp = (): void => {
    if (this.mouseMoveHandler && this.mouseUpHandler) {
      window.addEventListener("mousemove", this.mouseMoveHandler);
      window.addEventListener("mouseup", this.mouseUpHandler);
    }
  };

  addEventListenerMoveAndUpForHeight = () => {
    if (this.mouseMoveHeightHandler && this.mouseUpHeightHandler) {
      window.addEventListener("mousemove", this.mouseMoveHeightHandler);
      window.addEventListener("mouseup", this.mouseUpHeightHandler);
    }
  };

  handleMouseUp = (event: MouseEvent): void => {
    event.preventDefault();
    KMainFunctions.hideResizeWrapper();
    this.removeEventListeners();
    if (this.state.rulerWidth) {
      this.setScreenCanvasWidth(this.state.rulerWidth);
    }
    this.setState({ isMouseDown: false, rulerWidth: undefined });
  };

  handleMouseUpForHeight = (event: MouseEvent) => {
    event.preventDefault();
    KMainFunctions.hideResizeWrapper();
    this.removeEventListeners();
    if (this.state.rulerHeight) {
      this.setScreenCanvasHeight(this.state.rulerHeight);
    }
    this.setState({ isMouseDownToHeightButton: false, rulerHeight: undefined });
  };

  static getDerivedStateFromProps(nextProps: IDesignResizeButtonProps, prevState: IDesignResizeButonState) {
    return null;
  }

  componentDidUpdate(prevProps: IDesignResizeButtonProps): void {
    if (prevProps.zoomScale !== this.props.zoomScale) {
      this.setkuika_resizeSectionPosition(this.props.zoomScale);
    }

    const activeCanvasIFrame = this.getLayoutFrame() as HTMLIFrameElement;
    const activeCanvasIFrameHeight = activeCanvasIFrame?.getBoundingClientRect().height;

    if (
      prevProps.sd.sdId !== this.props.sd.sdId &&
      this.props.sd.dashboardState === DashboardState.reportDesigner &&
      activeCanvasIFrameHeight &&
      activeCanvasIFrameHeight > 0
    ) {
      this.setkuika_resizeHeightSectionPosition();
    }
  }

  onChangeResize = () => {
    if (this.debounceResize) {
      this.debounceResize();
    }
  };

  handleMouseOver = (): void => {
    this.setState({ isMouseOver: true });
  };

  handleMouseOut = (): void => {
    this.setState({ isMouseOver: false });
  };

  handleMouseOverToHeightButton = () => {
    this.setState({ isMouseOverToHeightButton: true });
  };

  handleMouseOutToHeightButton = () => {
    this.setState({ isMouseOverToHeightButton: false });
  };

  handleMouseDown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    event.preventDefault();
    KMainFunctions.showResizeWrapper();
    this.setkuika_resizeSectionPosition();
    this.setRulerWidthAndAttachMouseMoveAndUp(true);
  };

  handleMouseDownForHeight = (event: any) => {
    event.preventDefault();
    this.setkuika_resizeHeightSectionPosition();
    this.setRulerHeightAndAtachMouseMoveAndUp(true);
  };

  handleMouseMove = (event: MouseEvent): void => {
    event.preventDefault();
    this.setKuikaDesignAreaWidth(event.clientX);
    KMainFunctions.showResizeWrapper();
    this.setkuika_resizeSectionPosition();
    this.setRulerWidthAndAttachMouseMoveAndUp();
    this.onChangeResize();
  };

  handleMouseMoveForHeight = (event: any) => {
    event.preventDefault();
    this.setKuikaDesignAreaHeight(event.clientY);
    KMainFunctions.showResizeWrapper();
    this.setkuika_resizeHeightSectionPosition();
    this.setRulerHeightAndAtachMouseMoveAndUp();
  };

  setRulerWidthAndAttachMouseMoveAndUp = (isMouseDowned: boolean = false): void => {
    const layoutFrame = this.getLayoutFrame();
    const kuika_designArea = this.getDesignArea();
    const kuika_resizeSectionDiv = this.getResizeSectionDiv();
    let rulerWidth: number = 0;

    if (!layoutFrame || !kuika_designArea || !kuika_resizeSectionDiv) {
      return;
    }

    if (this.props.zoomScale <= 1) {
      rulerWidth = Math.ceil(kuika_designArea.getBoundingClientRect().width - 24);
    } else {
      rulerWidth = Math.ceil(layoutFrame.getBoundingClientRect().width / this.props.zoomScale) - 5;
    }

    if (isMouseDowned === true) {
      this.setState({ isMouseDown: isMouseDowned, rulerWidth }, this.addEventListenerMoveAndUp);
    } else {
      this.setState({ rulerWidth }, this.addEventListenerMoveAndUp);
    }
  };

  setScreenCanvasWidth = (width: number) => {
    // A report's size comes from its paper, and the pair written here is what the code
    // generators fall back to — so a drag must never land on a report record. The width handle
    // is not rendered in the Report Designer today; this keeps that true if it ever is.
    if (this.props.sd.isLoadedScreenReport?.() === true) {
      return;
    }
    const {
      sd: { project, updateProject }
    } = this.props;
    const { screenCanvasSizes } = project;
    const screen = screenCanvasSizes?.screens?.find((s) => s.id === this.props.sd.sdId);
    if (width && (screenCanvasSizes.width !== width || (screen && screen.width !== width))) {
      const canvasSizeByScreen: IScreenCanvasSize = {
        id: this.props.sd.sdId,
        width,
        deviceType: undefined,
        device: this.deviceList[0]
      };
      if (!screenCanvasSizes?.screens) screenCanvasSizes.screens = [];
      if (screen) {
        screen.width = width;
        screen.device = this.deviceList[0];
        screen.deviceType = undefined;
      } else {
        screenCanvasSizes.screens.push(canvasSizeByScreen);
      }
      updateProject({ ...project, screenCanvasSizes });
    }
  };

  setScreenCanvasHeight = (height: number) => {
    // Deliberately NOT gated on the loaded screen being a report: its handle is the report
    // designer's own (currently not rendered). If it comes back it must write the paper too,
    // or the page and the paper-size dropdown will disagree.
    height = Math.ceil(height);
    const {
      sd: { project, updateProject }
    } = this.props;
    const { screenCanvasSizes } = project;
    const screen = screenCanvasSizes?.screens?.find((s) => s.id === this.props.sd.sdId);
    if (height && screen && screen.height !== height) {
      const canvasSizeByScreen: IScreenCanvasSize = {
        id: this.props.sd.sdId,
        height
      };
      if (!screenCanvasSizes?.screens) screenCanvasSizes.screens = [];
      if (screen) {
        screen.height = height;
      } else {
        screenCanvasSizes.screens.push(canvasSizeByScreen);
      }
      updateProject({ ...project, screenCanvasSizes });
    }
  };

  setRulerHeightAndAtachMouseMoveAndUp = (isMouseDowned: boolean = false) => {
    const layoutFrame = this.getLayoutFrame();
    const kuika_designArea = this.getDesignArea();
    const kuika_resizeHeightSectionDiv = this.getResizeHeightSectionDiv();
    const height = kuika_designArea?.getBoundingClientRect().height;

    if (height) {
      const rulerHeight = Math.ceil(height);
      if (layoutFrame && kuika_designArea && kuika_resizeHeightSectionDiv) {
        if (isMouseDowned === true) {
          this.setState(
            { isMouseDownToHeightButton: isMouseDowned, rulerHeight },
            this.addEventListenerMoveAndUpForHeight
          );
        } else {
          this.setState({ rulerHeight }, this.addEventListenerMoveAndUpForHeight);
        }
      }
    }
  };

  setKuikaDesignAreaWidth = (clientX: number): void => {
    let minWidth: number = 320;
    const maxWidth: number = 595;

    if (this.props.sd.dashboardState === DashboardState.reportDesigner) {
      minWidth = 100;
    }
    if (this.props && this.props.minWidth) {
      minWidth = this.props.minWidth;
    }

    minWidth += 24;

    const kuika_designArea = this.getDesignArea();

    if (kuika_designArea) {
      const x = kuika_designArea.getBoundingClientRect().x - 0;
      let width = Math.ceil(clientX - x);
      if (width < minWidth) {
        width = minWidth;
      }
      if (width > maxWidth && this.props.sd.dashboardState === DashboardState.reportDesigner) {
        width = maxWidth;
      }

      kuika_designArea.style.width = `${width}px`;
    }
  };

  setKuikaDesignAreaHeight = (clientY: any) => {
    let minHeight = 150;
    if (this.props && this.props.minHeight) {
      minHeight = this.props.minHeight;
    }
    minHeight += 24;

    const kuika_designArea = this.getDesignArea();
    const activeCanvasIFrame = this.getLayoutFrame() as HTMLIFrameElement;

    if (kuika_designArea && activeCanvasIFrame) {
      const y = activeCanvasIFrame.getBoundingClientRect().y + 24;
      let height = Math.ceil(clientY - y);
      if (height < minHeight) {
        height = minHeight;
      }
      if (height > window.innerHeight - 120) {
        height = window.innerHeight - 120;
      }
      activeCanvasIFrame.style.height = `${height}px`;
      kuika_designArea.style.height = `${height}px`;
    }
  };

  setkuika_resizeSectionPosition = (zoomScale: number = this.props.zoomScale): void => {
    const kuika_designArea = this.getDesignArea();
    const kuika_resizeSectionDiv = this.getResizeSectionDiv();

    if (!kuika_designArea || !kuika_resizeSectionDiv) {
      return;
    }

    let kuika_resizeSectionRight = -1 + 2.3 * zoomScale;

    if (zoomScale <= 1) {
      kuika_resizeSectionRight =
        ((100 - zoomScale * 100) / 2 / 100) * kuika_designArea.getBoundingClientRect().width + -1 * zoomScale;
    }

    kuika_resizeSectionDiv.style.right = `${kuika_resizeSectionRight}px`;

    const kuika_designRuler = this.getDesignRuler();

    if (kuika_designRuler) {
      if (zoomScale < 1) {
        kuika_designRuler.style.left = `${kuika_resizeSectionRight - 24 / 2}px`;
      } else {
        kuika_designRuler.style.left = "0px";
      }
    }
  };

  setkuika_resizeHeightSectionPosition = () => {
    const kuika_designArea = this.getDesignArea();
    const activeCanvasIFrame = this.getLayoutFrame() as HTMLIFrameElement;
    const activeCanvasIFrameHeight = activeCanvasIFrame?.getBoundingClientRect().height;

    if (kuika_designArea) {
      const kuika_resizeHeightSectionDiv = this.getResizeHeightSectionDiv();
      if (kuika_resizeHeightSectionDiv) {
        kuika_resizeHeightSectionDiv.style.top = `${activeCanvasIFrameHeight - 18}px`;
      }
    }
  };

  render() {
    let verticalLineCount: number = 0;

    if (this.state.rulerWidth && this.state.rulerWidth > 0) {
      verticalLineCount = Math.ceil(this.state.rulerWidth / 10);

      if (verticalLineCount < 1) {
        verticalLineCount = 1;
      }
    }

    if (this.props.zoomScale > 1) {
      verticalLineCount += 2;
    }

    return (
      <>
        <div
          className={
            this.state.rulerWidth && this.state.rulerWidth > 0
              ? "kuika_desig-ruler-back-side-active"
              : "kuika_desig-ruler-back-side"
          }
        ></div>
        {this.props.sd.dashboardState !== DashboardState.reportDesigner && (
          <div
            id="kuika_resizeSectionDiv"
            onMouseDown={this.handleMouseDown}
            className={this.state.isMouseDown === true ? "kuika_resizeSectionButonHover" : "kuika_resizeSection"}
            style={{ right: "-1px" }}
          >
            <div className="kuika_resizeBtn">
              <svg
                onMouseOver={this.handleMouseOver}
                onMouseOut={this.handleMouseOut}
                xmlns="http://www.w3.org/2000/svg"
                width="7"
                height="36"
                viewBox="0 0 7 36"
              >
                <defs>
                  <filter id="tslcmgyi3a">
                    <feColorMatrix
                      in="SourceGraphic"
                      values="0 0 0 0 0.935461 0 0 0 0 0.957463 0 0 0 0 1.000000 0 0 0 1.000000 0"
                    />
                  </filter>
                </defs>
                <g fill="none" fillRule="evenodd">
                  <g>
                    <g transform="translate(-984 -409) translate(984.5 409)">
                      <rect
                        width="6"
                        height="36"
                        fill={
                          this.state.isMouseOver === true || this.state.isMouseDown === true ? "#246EFF" : "#c2c2c2"
                        }
                        rx="3"
                      />
                      <g filter="url(#tslcmgyi3a)">
                        <g fill="#858585" transform="translate(2 7)">
                          <rect width="2" height="2" rx="1" />
                          <rect width="2" height="2" y="5" rx="1" />
                          <rect width="2" height="2" y="10" rx="1" />
                          <rect width="2" height="2" y="15" rx="1" />
                          <rect width="2" height="2" y="20" rx="1" />
                        </g>
                      </g>
                    </g>
                  </g>
                </g>
              </svg>
            </div>
            {this.state.rulerWidth && this.state.rulerWidth > 0 && (
              <>
                <div className="kuika_design-ruler-value">
                  <div className="kuika_design-ruler-value-label">
                    {this.state.rulerWidth && this.state.rulerWidth > 0
                      ? this.props.zoomScale > 1
                        ? Math.ceil(this.state.rulerWidth)
                        : `${Math.ceil(this.state.rulerWidth)}px`
                      : ""}
                  </div>
                </div>
                <div className="kuika_design-ruler-value-edge"></div>
              </>
            )}
            {this.state.rulerHeight && this.state.rulerHeight > 0 && (
              <>
                <div className="kuika_design-ruler-value-height">
                  <div className="kuika_design-ruler-value-label">
                    {this.state.rulerHeight && this.state.rulerHeight > 0
                      ? this.props.zoomScale > 1
                        ? Math.ceil(this.state.rulerHeight)
                        : `${Math.ceil(this.state.rulerHeight)}px`
                      : ""}
                  </div>
                </div>
                <div className="kuika_design-ruler-value-edge-height"></div>
              </>
            )}
          </div>
        )}
        {/* {this.props.sd.dashboardState === DashboardState.reportDesigner && (
          <div
            id="kuika_resizeHeightSectionDiv"
            className="kuika_resizeHeightSectionDiv"
            onMouseDown={this.handleMouseDownForHeight}
          >
            <svg
              onMouseOver={this.handleMouseOverToHeightButton}
              onMouseOut={this.handleMouseOutToHeightButton}
              xmlns="http://www.w3.org/2000/svg"
              width="7"
              height="36"
              viewBox="0 0 7 36"
            >
              <defs>
                <filter id="tslcmgyi3a">
                  <feColorMatrix
                    in="SourceGraphic"
                    values="0 0 0 0 0.935461 0 0 0 0 0.957463 0 0 0 0 1.000000 0 0 0 1.000000 0"
                  />
                </filter>
              </defs>
              <g fill="none" fillRule="evenodd">
                <g>
                  <g transform="translate(-984 -409) translate(984.5 409)">
                    <rect
                      width="6"
                      height="36"
                      fill={
                        this.state.isMouseOverToHeightButton === true || this.state.isMouseDownToHeightButton === true
                          ? "#246EFF"
                          : "#c2c2c2"
                      }
                      rx="3"
                    />
                    <g filter="url(#tslcmgyi3a)">
                      <g fill="#858585" transform="translate(2 7)">
                        <rect width="2" height="2" rx="1" />
                        <rect width="2" height="2" y="5" rx="1" />
                        <rect width="2" height="2" y="10" rx="1" />
                        <rect width="2" height="2" y="15" rx="1" />
                        <rect width="2" height="2" y="20" rx="1" />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </svg>
          </div>
        )} */}

        <div
          className={
            this.props.zoomScale < 1 && this.state.rulerWidth && this.state.rulerWidth > 0
              ? "kuika_design-resizer-active-zoom-scale-less-than-one"
              : this.props.zoomScale >= 1 && this.state.rulerWidth && this.state.rulerWidth > 0
                ? "kuika_design-resizer-active"
                : "kuika_design-resizer"
          }
        >
          <div
            id="kuika_design-ruler"
            style={{
              width: this.state.rulerWidth,
              transformOrigin: "left",
              transform: `scaleX(${this.props.zoomScale})`
            }}
            className={
              this.state.rulerWidth && this.state.rulerWidth > 0 ? "kuika_design-ruler-active" : "kuika_design-ruler"
            }
          >
            {this.state.rulerWidth &&
              this.props.zoomScale &&
              this.props.zoomScale > 0 &&
              this.state.rulerWidth > 0 &&
              Array.apply(null, Array(verticalLineCount)).map((obj, i) => (
                <React.Fragment key={i.toString()}>
                  <div key={`${i.toString()}_10`} className="kuika_design-ruler-10"></div>
                  <div key={`${i.toString()}_05`} className="kuika_design-ruler-5"></div>
                </React.Fragment>
              ))}
          </div>
        </div>
      </>
    );
  }
}
